import styled from 'styled-components';
import { FormattedMessage, defineMessages } from 'react-intl';
import {
  Heading,
  H2,
  Container,
  ProductCard,
  Text,
  Image,
  Feature,
  Link,
  Flex,
  Box,
  Anchor
} from '../../components';
import { openDataQuery } from '../../core-types';
import { BlockContent } from '../../components/BlockContent';

type Props = {
  data: openDataQuery;
  showAppIntegration: boolean;
  showGBFS: boolean | null;
  showTripHistory: boolean | null;
};

export const messages = defineMessages({
  showcase: {
    id: 'modules.OpenData.showcase',
    defaultMessage: 'Showcase'
  },
  realtime: {
    id: 'modules.OpenData.realtimeButton.title',
    defaultMessage: 'Realtime'
  },
  realtimeDescription: {
    id: 'modules.OpenData.realtimeButton.description',
    defaultMessage:
      'Availability of stations and bikes, in live updated JSON feeds'
  },
  historical: {
    id: 'modules.OpenData.historicalButton.title',
    defaultMessage: 'Historical'
  },
  historicalDescription: {
    id: 'modules.OpenData.historicalButton.description',
    defaultMessage:
      'Monthly data of all trips published in CSV and JSON formats'
  },
  app: {
    id: 'modules.OpenData.appButton.title',
    defaultMessage: 'App integration'
  },
  appDescription: {
    id: 'modules.OpenData.appButton.description',
    defaultMessage: 'Integrate with our apps'
  },
  licence: {
    id: 'modules.OpenData.licence',
    defaultMessage: 'The data is published under the {licence}'
  }
});

const OpenDataWrapper = styled.div`
  max-width: 50em;
  margin-left: auto;
  margin-right: auto;
`;

const OpenData = ({
  data,
  showGBFS,
  showTripHistory,
  showAppIntegration
}: Props) => {
  if (!data.openData) {
    return null;
  }
  const { openData } = data;
  return (
    <Container mt={6}>
      <OpenDataWrapper>
        <Heading textAlign="center" mb={5}>
          {openData.title}
        </Heading>
        {openData.description && (
          <Text lead mb={5} textAlign="center">
            <BlockContent blocks={openData.description} />
          </Text>
        )}
        <Flex flexWrap="wrap" mt={7} justifyContent="center">
          {showGBFS && (
            <Box
              width={[1, 1, 1 / 2, 1 / 2, 1 / 3]}
              pr={[0, 0, 4, 4, 4]}
              pb={[4, 4, 4, 4, 0]}
            >
              <Link route="open-data.realtime" passHref>
                <Anchor>
                  <ProductCard>
                    <H2>
                      <FormattedMessage {...messages.realtime} />
                    </H2>
                    <Text>
                      <FormattedMessage {...messages.realtimeDescription} />
                    </Text>
                  </ProductCard>
                </Anchor>
              </Link>
            </Box>
          )}
          {showTripHistory && (
            <Box
              width={[1, 1, 1 / 2, 1 / 2, 1 / 3]}
              pr={[0, 0, 0, 0, 4]}
              pb={[4, 4, 4, 4, 0]}
            >
              <Link route="open-data.historical" passHref>
                <Anchor>
                  <ProductCard>
                    <H2>
                      <FormattedMessage {...messages.historical} />
                    </H2>
                    <Text>
                      <FormattedMessage {...messages.historicalDescription} />
                    </Text>
                  </ProductCard>
                </Anchor>
              </Link>
            </Box>
          )}
          {showAppIntegration && (
            <Box width={[1, 1, 1 / 2, 1 / 2, 1 / 3]}>
              <Link route="open-data.app" passHref>
                <Anchor>
                  <ProductCard>
                    <H2>
                      <FormattedMessage {...messages.app} />
                    </H2>
                    <Text>
                      <FormattedMessage {...messages.appDescription} />
                    </Text>
                  </ProductCard>
                </Anchor>
              </Link>
            </Box>
          )}
        </Flex>
        {openData.licence && openData.licence.licence_title && (
          <Text mt={6}>
            {openData.licence.licence_url ? (
              <FormattedMessage
                {...messages.licence}
                values={{
                  licence: (
                    <Anchor href={openData.licence.licence_url}>
                      {openData.licence.licence_title}
                    </Anchor>
                  )
                }}
              />
            ) : (
              <FormattedMessage
                {...messages.licence}
                values={{ licence: openData.licence.licence_title }}
              />
            )}
          </Text>
        )}

        {openData.showcases && openData.showcases.length > 0 && (
          <>
            <H2 textAlign="center" mb={5} mt={8}>
              <FormattedMessage {...messages.showcase} />
            </H2>
            {openData.showcaseDescription && (
              <Text lead mb={5}>
                <BlockContent blocks={openData.showcaseDescription} />
              </Text>
            )}
            {openData.showcases.map(showcase => {
              if (!showcase) {
                return null;
              }
              return (
                <Feature
                  key={showcase._key}
                  heading={showcase.title || ''}
                  media={
                    showcase.image && showcase.image.url ? (
                      <Image src={showcase.image.url} />
                    ) : null
                  }
                >
                  <BlockContent blocks={showcase.description} />
                  {showcase.url && (
                    <Anchor href={showcase.url}>Link to project</Anchor>
                  )}
                </Feature>
              );
            })}
          </>
        )}
      </OpenDataWrapper>
    </Container>
  );
};

export default OpenData;
